/**
 * TDLS Builder — Logo Upload Client
 *
 * Handles uploading logo files from the builder to the Netlify function,
 * with progress tracking and retry logic.
 *
 * Usage:
 *   const result = await BuilderUpload.upload(file);
 *   // result = { fileId, url, thumbnailUrl, filename, size }
 */

window.__BUILDER_UPLOAD = (function () {

  // ── Configuration ──────────────────────────────────────────────
  // Set this to your Netlify site URL after deployment
  var API_BASE = '';  // e.g. 'https://tdls-uploads.netlify.app'
  var UPLOAD_ENDPOINT = '/api/upload';
  var AUTH_TOKEN = '';  // Set via setConfig() — matches BUILDER_UPLOAD_SECRET

  var MAX_FILE_SIZE = 25 * 1024 * 1024; // 25 MB (must match server)
  var ALLOWED_EXT = ['.png', '.jpg', '.jpeg', '.webp', '.svg'];

  // ── State ──────────────────────────────────────────────────────
  var _onProgress = null; // callback(percent: number)

  // ── Config ─────────────────────────────────────────────────────
  function setConfig(opts) {
    if (opts.apiBase) API_BASE = opts.apiBase.replace(/\/$/, '');
    if (opts.authToken) AUTH_TOKEN = opts.authToken;
    if (opts.onProgress) _onProgress = opts.onProgress;
  }

  // ── Validation ─────────────────────────────────────────────────
  function validateFile(file) {
    if (!file) return 'No file selected';
    if (file.size > MAX_FILE_SIZE) {
      return 'File is too large (max ' + Math.round(MAX_FILE_SIZE / 1024 / 1024) + ' MB)';
    }
    var name = file.name || '';
    var dot = name.lastIndexOf('.');
    var ext = dot >= 0 ? name.slice(dot).toLowerCase() : '';
    if (!ext || ALLOWED_EXT.indexOf(ext) === -1) {
      return 'File type "' + ext + '" not accepted. Use: ' + ALLOWED_EXT.join(', ');
    }
    return null; // valid
  }

  // ── Upload ─────────────────────────────────────────────────────
  /**
   * Upload a File object to the Netlify function.
   * @param {File} file
   * @returns {Promise<{ fileId, url, thumbnailUrl, filename, size }>}
   */
  function upload(file) {
    return new Promise(function (resolve, reject) {
      var err = validateFile(file);
      if (err) return reject(new Error(err));

      if (!API_BASE) return reject(new Error('Upload API not configured. Call setConfig({ apiBase: "..." }) first.'));

      var formData = new FormData();
      formData.append('logo', file, file.name);

      var xhr = new XMLHttpRequest();
      xhr.open('POST', API_BASE + UPLOAD_ENDPOINT, true);

      // Auth header
      if (AUTH_TOKEN) {
        xhr.setRequestHeader('X-Builder-Token', AUTH_TOKEN);
      }

      // Progress tracking
      if (xhr.upload && _onProgress) {
        xhr.upload.addEventListener('progress', function (e) {
          if (e.lengthComputable) {
            _onProgress(Math.round((e.loaded / e.total) * 100));
          }
        });
      }

      xhr.onload = function () {
        if (xhr.status >= 200 && xhr.status < 300) {
          try {
            var data = JSON.parse(xhr.responseText);
            if (data.success) {
              resolve(data);
            } else {
              reject(new Error(data.error || 'Upload failed'));
            }
          } catch (e) {
            reject(new Error('Invalid response from upload server'));
          }
        } else {
          try {
            var errData = JSON.parse(xhr.responseText);
            reject(new Error(errData.error || 'Upload failed (HTTP ' + xhr.status + ')'));
          } catch (e) {
            reject(new Error('Upload failed (HTTP ' + xhr.status + ')'));
          }
        }
      };

      xhr.onerror = function () {
        reject(new Error('Network error — could not reach upload server'));
      };

      xhr.ontimeout = function () {
        reject(new Error('Upload timed out'));
      };

      xhr.timeout = 120000; // 2 minutes
      xhr.send(formData);
    });
  }

  /**
   * Upload with automatic retry (up to 2 retries).
   * @param {File} file
   * @param {number} [maxRetries=2]
   * @returns {Promise<{ fileId, url, thumbnailUrl, filename, size }>}
   */
  function uploadWithRetry(file, maxRetries) {
    maxRetries = maxRetries || 2;
    var attempt = 0;

    function tryUpload() {
      attempt++;
      return upload(file).catch(function (err) {
        if (attempt <= maxRetries && !err.message.includes('not accepted') && !err.message.includes('too large')) {
          // Retry on network/server errors, not validation errors
          return new Promise(function (resolve) {
            setTimeout(resolve, 1000 * attempt); // backoff
          }).then(tryUpload);
        }
        throw err;
      });
    }

    return tryUpload();
  }

  // ── Helpers ────────────────────────────────────────────────────
  /**
   * Get the full URL for a previously uploaded file.
   * @param {string} fileId
   * @param {boolean} [thumbnail=false]
   * @returns {string}
   */
  function getFileUrl(fileId, thumbnail) {
    var url = API_BASE + UPLOAD_ENDPOINT + '?id=' + encodeURIComponent(fileId);
    if (thumbnail) url += '&thumb=1';
    return url;
  }

  // ── Public API ─────────────────────────────────────────────────
  return {
    setConfig: setConfig,
    validateFile: validateFile,
    upload: upload,
    uploadWithRetry: uploadWithRetry,
    getFileUrl: getFileUrl,
    getApiBase: function () { return API_BASE; },
    getAuthToken: function () { return AUTH_TOKEN; },

    /**
     * Save serialized builder state to server.
     * @param {Object} stateObj — output of serializeState()
     * @returns {Promise<string>} — state ID
     */
    saveState: function (stateObj) {
      if (!API_BASE) return Promise.reject(new Error('Upload API not configured'));
      return fetch(API_BASE + '/api/build-state', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Builder-Token': AUTH_TOKEN
        },
        body: JSON.stringify(stateObj)
      }).then(function (resp) {
        if (!resp.ok) throw new Error('Save state failed (HTTP ' + resp.status + ')');
        return resp.json();
      }).then(function (data) {
        if (!data.success || !data.stateId) throw new Error('Save state failed');
        return data.stateId;
      });
    },

    /**
     * Load serialized builder state from server.
     * @param {string} stateId
     * @returns {Promise<Object>} — parsed state object
     */
    loadState: function (stateId) {
      if (!API_BASE) return Promise.reject(new Error('Upload API not configured'));
      return fetch(API_BASE + '/api/build-state?id=' + encodeURIComponent(stateId)).then(function (resp) {
        if (!resp.ok) throw new Error('Load state failed (HTTP ' + resp.status + ')');
        return resp.json();
      });
    },
  };

})();
